// src/pages/business/BusinessRegistrationPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, Building2, Phone, FileText, Camera,
  MapPin, CheckCircle, ChevronRight, ChevronLeft,
  Rocket, Shield, Zap, Globe, Loader2, X, Upload,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

const CATEGORIES = [
  'Retail & Commerce','Food & Beverage','Technology','Healthcare',
  'Education','Finance','Real Estate','Manufacturing','Services','Agriculture',
];

const STEPS = [
  { title: 'Business Details',   desc: 'Tell us about your amazing business',        icon: Building2 },
  { title: 'Contact Info',       desc: 'How can customers reach you?',               icon: Phone },
  { title: 'Legal Documents',    desc: 'Optional verification documents',            icon: FileText },
  { title: 'Branding & Photos',  desc: 'Make your business shine',                   icon: Camera },
];

interface FormState {
  businessName: string;
  businessCategory: string;
  businessDescription: string;
  locationAddress: string;
  latitude: string;
  longitude: string;
  ownerFullName: string;
  primaryContactPhone: string;
  whatsappNumber: string;
  businessEmail: string;
  nationalID: string;
  kraPin: string;
  businessRegistrationNumber: string;
  businessLogo: File | null;
  premisesPhoto: File | null;
  ownerPhoto: File | null;
  kraPinCertificate: File | null;
  registrationCertificate: File | null;
}

const initial: FormState = {
  businessName: '', businessCategory: '', businessDescription: '',
  locationAddress: '', latitude: '', longitude: '',
  ownerFullName: '', primaryContactPhone: '', whatsappNumber: '', businessEmail: '',
  nationalID: '', kraPin: '', businessRegistrationNumber: '',
  businessLogo: null, premisesPhoto: null, ownerPhoto: null,
  kraPinCertificate: null, registrationCertificate: null,
};

// ── File picker row ────────────────────────────────────────────────────────────
const FilePicker: React.FC<{
  label: string;
  file: File | null;
  accept: string;
  onChange: (f: File | null) => void;
}> = ({ label, file, accept, onChange }) => (
  <div className="mb-5">
    <p className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">{label}</p>
    {file ? (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300">
        {file.type.startsWith('image/') ? (
          <img src={URL.createObjectURL(file)} alt="" className="w-14 h-14 rounded-lg object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileText size={24} className="text-primary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 truncate">{file.name}</p>
          <p className="text-xs text-emerald-600">✓ Uploaded successfully</p>
        </div>
        <button onClick={() => onChange(null)} className="p-1">
          <X size={18} className="text-red-500" />
        </button>
      </div>
    ) : (
      <label className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-light-border dark:border-dark-border cursor-pointer hover:border-primary transition-colors">
        <Upload size={18} className="text-primary" />
        <span className="text-sm font-medium text-primary">Choose File</span>
        <input type="file" accept={accept} className="hidden" onChange={e => onChange(e.target.files?.[0] ?? null)} />
      </label>
    )}
  </div>
);

// ── Input ──────────────────────────────────────────────────────────────────────
const Input: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean; error?: string;
  multiline?: boolean;
}> = ({ label, value, onChange, placeholder, type = 'text', required, error, multiline }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-light-text dark:text-dark-text mb-1.5">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {multiline ? (
      <textarea
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3}
        className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
      />
    ) : (
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

export const BusinessRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [completed, setCompleted] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);

  const set = (field: keyof FormState, value: any) => {
    setForm(p => ({ ...p, [field]: value }));
    setErrors(p => ({ ...p, [field]: undefined }));
  };

  const validate = (s: number): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (s === 0) {
      if (!form.businessName.trim()) e.businessName = 'Business name is required.';
      if (!form.businessCategory) e.businessCategory = 'Category is required.';
      if (!form.locationAddress) e.locationAddress = 'Location must be captured.';
    }
    if (s === 1) {
      if (!form.primaryContactPhone.trim()) e.primaryContactPhone = 'Primary contact is required.';
      if (form.businessEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.businessEmail))
        e.businessEmail = 'Invalid email address.';
    }
    if (s === 2) {
      if (form.kraPin && !/^[A-Z]\d{9}[A-Z]$/i.test(form.kraPin)) e.kraPin = 'Invalid KRA PIN (e.g. A123456789Z).';
      if (form.nationalID && (form.nationalID.length < 7 || form.nationalID.length > 8))
        e.nationalID = 'National ID should be 7–8 digits.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validate(step)) return;
    setCompleted(p => [...new Set([...p, step])]);
    setStep(s => s + 1);
  };

  const handleBack = () => setStep(s => s - 1);

  const handleGetLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const { latitude, longitude } = pos.coords;
        set('latitude', String(latitude));
        set('longitude', String(longitude));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          set('locationAddress', data.display_name || `${latitude}, ${longitude}`);
        } catch {
          set('locationAddress', `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
        }
        setLocating(false);
      },
      () => { toastError('Location error', 'Could not get location. Please try again.'); setLocating(false); }
    );
  };

  const handleSubmit = async () => {
    if (!validate(step)) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('businessName', form.businessName);
      fd.append('businessCategory', form.businessCategory);
      if (form.businessDescription) fd.append('businessDescription', form.businessDescription);
      if (form.latitude) fd.append('latitude', form.latitude);
      if (form.longitude) fd.append('longitude', form.longitude);
      if (form.locationAddress) fd.append('locationAddress', form.locationAddress);
      if (form.ownerFullName) fd.append('ownerFullName', form.ownerFullName);
      fd.append('primaryContactPhone', form.primaryContactPhone);
      if (form.whatsappNumber) fd.append('whatsappNumber', form.whatsappNumber);
      if (form.businessEmail) fd.append('businessEmail', form.businessEmail);
      if (form.nationalID) fd.append('nationalID', form.nationalID);
      if (form.kraPin) fd.append('kraPin', form.kraPin);
      if (form.businessRegistrationNumber) fd.append('businessRegistrationNumber', form.businessRegistrationNumber);
      if (form.businessLogo) fd.append('businessLogo', form.businessLogo);
      if (form.premisesPhoto) fd.append('premisesPhoto', form.premisesPhoto);
      if (form.ownerPhoto) fd.append('ownerPhoto', form.ownerPhoto);
      if (form.kraPinCertificate) fd.append('kraPinCertificate', form.kraPinCertificate);
      if (form.registrationCertificate) fd.append('registrationCertificate', form.registrationCertificate);

      await apiClient.post('/businesses', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshProfile();
      success('Business Registered!', 'Your business has been successfully registered.');
      navigate('/business');
    } catch (err: any) {
      toastError('Submission failed', err?.response?.data?.message || err?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/business')} className="w-9 h-9 rounded-full bg-light-bg dark:bg-dark-bg flex items-center justify-center">
          <ArrowLeft size={20} className="text-primary" />
        </button>
        <div>
          <h1 className="text-xl font-extrabold text-light-text dark:text-dark-text">Register Business</h1>
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Join our thriving community</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-32">
        {/* Hero */}
        <div className="mt-4 p-6 rounded-2xl bg-primary text-white text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            <Rocket size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-extrabold mb-2">Launch Your Business Today</h2>
          <p className="text-white/85 text-sm leading-relaxed">Join 10,000+ successful businesses across Kenya. Fast, secure, and completely free!</p>
          <div className="flex justify-around mt-4 pt-4 border-t border-white/20">
            {[{ icon: Shield, label: 'Secure' }, { icon: Zap, label: 'Fast' }, { icon: Globe, label: 'Nationwide' }].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1 bg-white/15 px-4 py-2 rounded-2xl">
                <Icon size={20} className="text-white" />
                <span className="text-xs font-semibold text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step indicator */}
        <div className="mt-4 p-5 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">
          <div className="flex justify-between mb-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = completed.includes(i);
              const active = i === step;
              return (
                <div key={i} className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-3 mb-2 transition-all ${
                    done ? 'bg-emerald-500 border-emerald-500 scale-100' :
                    active ? 'bg-primary border-primary scale-110' :
                    'bg-light-bg dark:bg-dark-bg border-light-border dark:border-dark-border'
                  }`}>
                    {done ? <CheckCircle size={20} className="text-white" /> : <Icon size={16} className={active ? 'text-white' : 'text-light-subtle dark:text-dark-subtle'} />}
                  </div>
                  <span className={`text-[11px] font-semibold text-center leading-tight ${active ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-1.5 bg-light-border dark:bg-dark-border rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Step content */}
        <div className="mt-4 p-6 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">
          {/* Step header */}
          <div className="flex flex-col items-center text-center mb-6 pb-5 border-b border-light-border dark:border-dark-border">
            {React.createElement(STEPS[step].icon, { size: 32, className: 'text-primary mb-3' })}
            <h3 className="text-2xl font-extrabold text-light-text dark:text-dark-text">{STEPS[step].title}</h3>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">{STEPS[step].desc}</p>
          </div>

          {/* ── Step 0: Business Details ── */}
          {step === 0 && (
            <>
              <Input label="Business Name" value={form.businessName} onChange={v => set('businessName', v)} placeholder="e.g. Apex Retail Solutions" required error={errors.businessName} />

              <div className="mb-4">
                <p className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">Business Category <span className="text-red-500">*</span></p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => set('businessCategory', cat)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${
                        form.businessCategory === cat
                          ? 'bg-primary border-primary text-white'
                          : 'bg-light-bg dark:bg-dark-bg border-light-border dark:border-dark-border text-light-text dark:text-dark-text'
                      }`}>
                      {cat}
                    </button>
                  ))}
                </div>
                {errors.businessCategory && <p className="text-xs text-red-500 mt-1">{errors.businessCategory}</p>}
              </div>

              <Input label="Business Description (Optional)" value={form.businessDescription} onChange={v => set('businessDescription', v)} placeholder="Tell us what makes your business special..." multiline />

              <div className="mb-4">
                <p className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">Business Location <span className="text-red-500">*</span></p>
                <button onClick={handleGetLocation} disabled={locating}
                  className={`w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 font-bold text-sm transition-colors ${
                    form.locationAddress ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'border-primary bg-primary/5 text-primary'
                  }`}>
                  {locating ? <Loader2 size={20} className="animate-spin" /> : form.locationAddress ? <CheckCircle size={20} /> : <MapPin size={20} />}
                  {locating ? 'Getting your location...' : form.locationAddress ? '✓ Location captured' : '📍 Capture GPS Location'}
                </button>
                {form.locationAddress && (
                  <div className="flex items-start gap-2 mt-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                    <MapPin size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">{form.locationAddress}</p>
                  </div>
                )}
                {errors.locationAddress && <p className="text-xs text-red-500 mt-1">{errors.locationAddress}</p>}
              </div>
            </>
          )}

          {/* ── Step 1: Contact Info ── */}
          {step === 1 && (
            <>
              <Input label="Owner/Manager Full Name" value={form.ownerFullName} onChange={v => set('ownerFullName', v)} placeholder="e.g. Jane Doe" />
              <Input label="Primary Contact Number" value={form.primaryContactPhone} onChange={v => set('primaryContactPhone', v)} placeholder="+254 700 000 000" type="tel" required error={errors.primaryContactPhone} />
              <Input label="WhatsApp Business Number" value={form.whatsappNumber} onChange={v => set('whatsappNumber', v)} placeholder="+254 700 000 000" type="tel" />
              <Input label="Business Email" value={form.businessEmail} onChange={v => set('businessEmail', v)} placeholder="info@yourbusiness.co.ke" type="email" error={errors.businessEmail} />
            </>
          )}

          {/* ── Step 2: Legal Documents ── */}
          {step === 2 && (
            <>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/10 border-l-4 border-primary mb-5">
                <span className="text-primary text-lg">ℹ️</span>
                <p className="text-xs text-primary leading-relaxed">These details are optional but recommended for verification and building trust with customers.</p>
              </div>
              <Input label="National ID Number" value={form.nationalID} onChange={v => set('nationalID', v)} placeholder="12345678" error={errors.nationalID} />
              <Input label="KRA PIN" value={form.kraPin} onChange={v => set('kraPin', v.toUpperCase())} placeholder="A123456789Z" error={errors.kraPin} />
              <Input label="Business Registration Number" value={form.businessRegistrationNumber} onChange={v => set('businessRegistrationNumber', v)} placeholder="BIZ/2024/123456" />
              <FilePicker label="KRA PIN Certificate" file={form.kraPinCertificate} accept=".pdf,image/*" onChange={f => set('kraPinCertificate', f)} />
              <FilePicker label="Business Registration Certificate" file={form.registrationCertificate} accept=".pdf,image/*" onChange={f => set('registrationCertificate', f)} />
            </>
          )}

          {/* ── Step 3: Branding & Photos ── */}
          {step === 3 && (
            <>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 mb-5">
                <span className="text-amber-500 text-lg">💡</span>
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">Adding photos increases customer trust by up to 85% and helps your business stand out!</p>
              </div>
              <FilePicker label="Business Logo" file={form.businessLogo} accept="image/*" onChange={f => set('businessLogo', f)} />
              <FilePicker label="Business Premises Photo" file={form.premisesPhoto} accept="image/*" onChange={f => set('premisesPhoto', f)} />
              <FilePicker label="Owner/Manager Photo" file={form.ownerPhoto} accept="image/*" onChange={f => set('ownerPhoto', f)} />

              {/* Summary */}
              <div className="mt-4 p-4 rounded-xl bg-light-bg dark:bg-dark-bg border-2 border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-light-border dark:border-dark-border">
                  <CheckCircle size={22} className="text-emerald-500" />
                  <h4 className="font-extrabold text-light-text dark:text-dark-text">Registration Summary</h4>
                </div>
                {[
                  ['Business', form.businessName || 'Not specified'],
                  ['Category', form.businessCategory || 'Not specified'],
                  ['Contact', form.primaryContactPhone || 'Not specified'],
                  ['Location', form.locationAddress ? 'Captured ✓' : 'Not captured'],
                  ...(form.businessEmail ? [['Email', form.businessEmail]] : []),
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-1.5">
                    <span className="text-sm text-light-subtle dark:text-dark-subtle">{label}</span>
                    <span className="text-sm font-bold text-light-text dark:text-dark-text text-right max-w-[60%] truncate">{value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom nav controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border px-4 py-4 flex gap-3">
        <button onClick={handleBack} disabled={step === 0}
          className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle font-bold disabled:opacity-40">
          <ChevronLeft size={20} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={handleNext}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-primary text-white font-bold">
            Next Step <ChevronRight size={20} />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting}
            className="flex-[1.2] flex items-center justify-center gap-2 py-4 rounded-2xl bg-emerald-500 text-white font-bold disabled:opacity-60">
            {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : <><Rocket size={18} /> Launch Business!</>}
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessRegistrationPage;
